import "@github/relative-time-element";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Toaster, toast } from "react-hot-toast";
import { RouterProvider } from "react-router-dom";
import "./i18n";
import "./index.css";
import { ErrorBoundary } from "react-error-boundary";
import { AppSkeleton } from "@/components/AppSkeleton";
import Spinner from "@/components/Spinner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { InstanceProvider, useInstance } from "@/contexts/InstanceContext";
import { ViewProvider } from "@/contexts/ViewContext";
import { GlobalErrorFallback } from "@/core/error/ErrorBoundary";
import { queryClient } from "@/lib/query-client";
import { refreshAccessTokenWithManager } from "./connect";
import router from "./router";
import { applyLocaleEarly } from "./utils/i18n";
import { applyThemeEarly } from "./utils/theme";
import "leaflet/dist/leaflet.css";
// import "katex/dist/katex.min.css"; // DISABLED: Conflicts with $TICKER syntax

// Display build version info in console and toast
fetch("/build-info")
  .then((res) => res.json())
  .then((info) => {
    const version = `📦 HamJournal Build ${info.version} #${info.buildNumber} (${info.buildTime})`;
    console.log(`%c${version}`, "color: #4CAF50; font-weight: bold; font-size: 12px;");
    console.log("%cIf you see ticker issues, check if this is the latest build!", "color: #FF9800; font-size: 11px;");

    // Display build info as toast in top-right corner
    toast.success(version, {
      id: "build-info",
      duration: 5000, // 5 seconds
      style: {
        background: "#1e293b",
        color: "#e2e8f0",
        fontSize: "12px",
        fontFamily: "monospace",
      },
    });
  })
  .catch(() => console.warn("Unable to fetch build info"));

// Apply theme and locale early to prevent flash
applyThemeEarly();
applyLocaleEarly();

/**
 * Bootstrap sequence before React renders:
 *
 * 1. Silent refresh (if refresh cookie exists)
 *    - Restores access token to memory
 *    - Prevents 401 errors on startup
 *    - No blocking if no cookie present
 *
 * 2. Initialize contexts in parallel
 *    - Auth state (will use token from step 1)
 *    - Instance settings (no longer calls public GetInstanceProfile)
 *
 * 3. Render React app
 *    - All subsequent requests have valid token (if logged in)
 *    - No race conditions
 *    - Clean semantics (no spurious 401s)
 */
async function bootstrap() {
  // Step 1: Silent refresh before React renders
  // This prevents 401 errors during auth initialization
  console.debug("[bootstrap] Starting silent refresh...");
  try {
    await refreshAccessTokenWithManager();
    // Access token now in memory (if refresh cookie exists)
    console.debug("[bootstrap] Silent refresh completed successfully");
  } catch (error) {
    // No refresh cookie or expired - expected for first-time visitors
    // Continue normally, user will see login page
    console.debug("[bootstrap] Silent refresh failed (expected for logged-out users)", {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  // Step 2: Wait for DOM to be ready
  if (document.readyState === "loading") {
    await new Promise((resolve) => {
      document.addEventListener("DOMContentLoaded", resolve);
    });
  }

  return true;
}

// Inner component that initializes contexts
function AppInitializer({ children }: { children: React.ReactNode }) {
  const { isInitialized: authInitialized, initialize: initAuth } = useAuth();
  const { isInitialized: instanceInitialized, initialize: initInstance } = useInstance();
  const initStartedRef = useRef(false);

  // Initialize on mount - auth already has token from bootstrap
  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const init = async () => {
      // Parallel initialization
      // - Instance no longer calls public GetInstanceProfile endpoint
      // - Auth will not return 401 because token is in memory from bootstrap
      await Promise.all([initInstance(), initAuth()]);
    };
    init();
  }, [initAuth, initInstance]);

  if (!authInitialized || !instanceInitialized) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return <>{children}</>;
}

function Main() {
  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback} onReset={() => window.location.reload()}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <InstanceProvider>
            <ViewProvider>
              <AppInitializer>
                <RouterProvider router={router} />
                <Toaster position="top-right" />
              </AppInitializer>
            </ViewProvider>
          </InstanceProvider>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Bootstrap before rendering React
// Show loading skeleton during bootstrap to avoid white screen
function AppWithBootstrap() {
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  useEffect(() => {
    bootstrap()
      .then(() => {
        setIsBootstrapping(false);
      })
      .catch((error) => {
        console.error("Bootstrap failed:", error);
        setBootstrapError(error.message || "Failed to initialize app");
        setIsBootstrapping(false);
      });
  }, []);

  // Show skeleton during bootstrap
  if (isBootstrapping) {
    return <AppSkeleton />;
  }

  // Show error if bootstrap failed
  if (bootstrapError) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-red-500 mb-4">Failed to initialize application</p>
          <p className="text-sm text-muted-foreground">{bootstrapError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Bootstrap complete, render main app
  return <Main />;
}

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(<AppWithBootstrap />);
} else {
  console.error("Failed to find #root element");
}
